import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import Navbar from "./sections/Navbar";
import Footer from "./sections/Footer";

import "./scss/index.scss";
import { Suspense, lazy, useEffect } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { firebaseAuth } from "./utils/firebaseConfig";
import { useAppDispatch, useAppSelector } from "./app/hooks";
import { clearToasts, setToast, setUserStatus } from "./app/slices/AppSlice";
import { getUserPokemons } from "./app/reducers/getUserPokemons";
import { addListToDatabase } from "./app/reducers/addListToDatabase";
import { clearUserPokemons } from "./app/slices/PokemonSlice";
import { ToastContainer, ToastOptions, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "./components/Loader";
import ErrorBoundary from "./components/ErrorBoundary";
import { QueryClient, QueryClientProvider } from "react-query";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const Search = lazy(() => import("./pages/Search"));
const MyList = lazy(() => import("./pages/MyList"));
const About = lazy(() => import("./pages/About"));
const Compare = lazy(() => import("./pages/Compare"));
const Pokemon = lazy(() => import("./pages/Pokemon"));

export default function App() {
  const { toasts } = useAppSelector(({ app }) => app);
  const dispatch = useAppDispatch();
  useEffect(() => {
    const unsub = onAuthStateChanged(firebaseAuth, (currentUser) => {
      if (currentUser) {
        dispatch(setUserStatus({ email: currentUser.email as string }));
        dispatch(getUserPokemons());
      } else {
        dispatch(setUserStatus(undefined));
        dispatch(clearUserPokemons());
      }
    });
    return () => unsub();
  }, [dispatch]);
  // Flush any pending changes to Firestore when the tab is being hidden,
  // navigated away, or closed. Best-effort — works most of the time and
  // fires far less frequently than write-through would.
  useEffect(() => {
    const flush = () => {
      if (!firebaseAuth.currentUser) return;
      dispatch(addListToDatabase());
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flush();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", flush);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", flush);
    };
  }, [dispatch]);

  // Surface online/offline transitions as toasts.
  useEffect(() => {
    const onOffline = () =>
      dispatch(setToast("You're offline — showing cached Pokémon."));
    const onOnline = () => dispatch(setToast("Back online."));
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);
    return () => {
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
    };
  }, [dispatch]);

  useEffect(() => {
    if (toasts.length) {
      const toastOptions: ToastOptions = {
        position: "bottom-right",
        autoClose: 2000,
        pauseOnHover: true,
        draggable: true,
        theme: "dark",
      };
      toasts.forEach((message: string) => {
        toast(message, toastOptions);
      });
      dispatch(clearToasts());
    }
  }, [toasts, dispatch]);

  return (
    <QueryClientProvider client={queryClient}>
      <div className="main-container">
        <BrowserRouter>
          <div className="app">
            <Navbar />
            <ErrorBoundary>
              <Suspense fallback={<Loader />}>
                <Routes>
                  <Route element={<Search />} path="/search" />
                  <Route element={<MyList />} path="/list" />
                  <Route element={<About />} path="/about" />
                  <Route element={<Compare />} path="/compare" />
                  <Route element={<Pokemon />} path="/pokemon/:id" />
                  <Route element={<Navigate to="/pokemon/1" />} path="*" />
                </Routes>
              </Suspense>
            </ErrorBoundary>
            <Footer />
            <ToastContainer />
          </div>
        </BrowserRouter>
      </div>
    </QueryClientProvider>
  );
}
